import * as React from 'react';
import { useEffect, useState } from 'react';
import * as _ from 'lodash';
import { useOrgStore } from '../../../hooks/useOrgStore';
import { useAllowed } from '../../../hooks/useAllowed';
import { observer } from 'mobx-react';
import { CenterFormOrientation, CenterFormWidget } from '../../../widgets/layout/CenterFormWidget';
import { AppModel } from '@journeyapps-platform/lib-journey-core';
import { MigrateAppToGithubWidget } from './MigrateAppToGithubWidget';
import { MigrateAppToJourneyGitWidget } from './MigrateAppToJourneyGitWidget';
import { JourneyNavigationHandler, Routes } from '../../../routes';
import { SourceType } from '@journeyapps-platform/types-source-service/';
import { GitProviderWidget } from '../create-app/CreateAppStep2Widget';
import { MigrateAppToAzureWidget } from './MigrateAppToAzureWidget';
import { InputWrapperWidget } from '../../../widgets/inputs';
import styled from '@emotion/styled';
import { ParagraphWidget } from '../../../widgets/forms/ParagraphWidget';
import { AppModelAction } from '@journeyapps-platform/cardinal-catalog';

const getValidProviders = (app: AppModel) => {
  return _.chain(SourceType)
    .values()
    .filter((i) => i !== app?.model.source?.type)
    .value() as SourceType[];
};

export const ChangeSourceControlForm: React.FC<{ handler: JourneyNavigationHandler<Routes.APP_SOURCE_PROVIDER> }> =
  observer((props) => {
    const orgStore = useOrgStore();
    const { params, navigate } = props.handler;
    const [provider, setProvider] = useState<SourceType | null>(null);

    useEffect(() => {
      (async () => {
        const org = await orgStore.getOrg(params.org_id);
        const app = await org.getApp(params.app_id);
        if (app) {
          setProvider(getValidProviders(app)[0]);
        }
      })();
    }, []);

    // check for app
    let app: AppModel | null = orgStore.org(params.org_id)?.app(params?.app_id);

    // not allowed
    const isAllowed = useAllowed({ action: AppModelAction.UPDATE, resource: app });
    if (!app || !isAllowed || !provider) {
      return null;
    }

    return (
      <CenterFormWidget
        noLogo={true}
        primaryLabel="Convert Git provider"
        secondaryLabel={app ? `App: ${app.model.name}` : ''}
        orientation={CenterFormOrientation.ROW}
      >
        <S.Container>
          <S.Paragraph>Which Git provider would you like to migrate to?</S.Paragraph>
          <InputWrapperWidget>
            <GitProviderWidget
              validProviders={getValidProviders(app)}
              provider={provider}
              providerChanged={setProvider}
            />
          </InputWrapperWidget>
        </S.Container>
        <S.Container>
          {provider === SourceType.GitHub ? (
            <MigrateAppToGithubWidget
              createIntegration={() => {
                props.handler.navigate(Routes.CREATE_INTEGRATION, props.handler.params);
              }}
              close={() => {
                navigate(Routes.MANAGE_APPS, params);
              }}
              app={app}
            />
          ) : null}
          {provider === SourceType.Internal ? (
            <MigrateAppToJourneyGitWidget
              close={() => {
                navigate(Routes.MANAGE_APPS, params);
              }}
              app={app}
            />
          ) : null}
          {provider === SourceType.AzureDevops ? (
            <MigrateAppToAzureWidget
              createIntegration={() => {
                props.handler.navigate(Routes.CREATE_INTEGRATION, props.handler.params);
              }}
              close={() => {
                navigate(Routes.MANAGE_APPS, params);
              }}
              app={app}
            />
          ) : null}
        </S.Container>
      </CenterFormWidget>
    );
  });

namespace S {
  export const Container = styled.div`
    padding-bottom: 10px;
  `;

  export const Paragraph = styled(ParagraphWidget)`
    text-align: left;
    margin-bottom: 16px;
  `;
}
